import React from 'react';
import { PassAtKRunStatus, PassAtKRunProgressDTO } from '@scale/shared/types/passAtK';
import { Button, Flex, Text } from '@scale/scaleui-radix';

const NOT_RUNNING_STATUSES = [
  PassAtKRunStatus.Completed,
  PassAtKRunStatus.Error,
  PassAtKRunStatus.Cancelled,
];

// Time constants (in seconds)
const WARNING_TIME_LIMIT_SECONDS = 24 * 60 * 60; // 24 hours

export function ProgressBar({
  progress,
  onRetry,
  onReactivate,
  isUpdating = false,
}: {
  progress?: PassAtKRunProgressDTO;
  onRetry?: () => void;
  onReactivate?: () => void;
  isUpdating?: boolean;
}) {
  const [dots, setDots] = React.useState('...');
  const [smoothProgress, setSmoothProgress] = React.useState(0);
  const [elapsedTime, setElapsedTime] = React.useState(0);
  const startTimeRef = React.useRef<number | null>(null);

  // Animate dots
  React.useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '.' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Timer effect
  React.useEffect(() => {
    if (!progress?.compassWorkflowLastRunAt) {
      return;
    }

    startTimeRef.current = new Date(progress.compassWorkflowLastRunAt).getTime();

    const timer = setInterval(() => {
      if (startTimeRef.current) {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [progress?.compassWorkflowLastRunAt]);

  // Check if elapsed time exceeds warning limit
  const isOverTimeLimit = React.useMemo(() => {
    return elapsedTime > WARNING_TIME_LIMIT_SECONDS;
  }, [elapsedTime]);

  // Format elapsed time as mm:ss
  const formatElapsedTime = (seconds: number): string | null => {
    if (
      NOT_RUNNING_STATUSES.includes(progress?.status as PassAtKRunStatus) ||
      !progress?.compassWorkflowLastRunAt ||
      !seconds
    ) {
      return null;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTargetProgress = (): number => {
    if (!progress) return 0;
    if (progress.status === PassAtKRunStatus.Completed) {
      return 100;
    }
    return ((progress?.completedTasks ?? 0) / (progress?.totalTasks ?? 1)) * 100;
  };

  // Smooth progress animation
  React.useEffect(() => {
    const targetProgress = getTargetProgress();
    let animationFrame: number;

    const animate = () => {
      setSmoothProgress(current => {
        const diff = targetProgress - current;

        // Speed up animation if we need to catch up
        const step = Math.abs(diff) > 20 ? 2 : 0.5;

        // If we're close enough, snap to target
        if (Math.abs(diff) < 0.1) return targetProgress;

        // Only allow progress to increase, never decrease
        if (diff <= 0) return current;

        // Otherwise, move towards target
        return current + step;
      });

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [progress]);

  // Fake initial progress for better UX
  React.useEffect(() => {
    if (!progress) {
      // Start with quick progress to 25% then slow down
      const initialAnimation = setTimeout(() => {
        setSmoothProgress(25);
      }, 100);

      const slowProgress = setInterval(() => {
        setSmoothProgress(current => Math.min(current + 0.033, 50));
      }, 100);

      return () => {
        clearTimeout(initialAnimation);
        clearInterval(slowProgress);
      };
    }
  }, [progress]);

  const getProgressTitle = (progress?: PassAtKRunProgressDTO): string => {
    if (!progress) {
      return `Loading${dots}`;
    }

    if (progress.status === PassAtKRunStatus.Pending) {
      return `Scheduled${dots}`;
    }

    const progressText =
      progress.completedTasks !== undefined && progress.totalTasks !== undefined
        ? ` (${progress.completedTasks} / ${progress.totalTasks})`
        : '';
    if (progress.status === PassAtKRunStatus.Processing) {
      return `Processing Data${progressText}${dots}`;
    } else {
      const statusText =
        String(progress.status).charAt(0).toUpperCase() +
        String(progress.status).slice(1).toLowerCase();
      return `${statusText}${progressText}`;
    }
  };

  // Determine which action button to show and its properties
  const actionButton = React.useMemo(() => {
    if (!progress) return null;

    // Show reactivate button for runs over time limit that are still processing
    if (isOverTimeLimit && progress.status === PassAtKRunStatus.Processing && onReactivate) {
      return {
        text: 'Reactivate',
        onClick: onReactivate,
        color: 'accent' as const,
        variant: 'surface' as const,
      };
    }

    // Show retry button for error states or incomplete completion
    if (
      (progress.status === PassAtKRunStatus.Error && onRetry) ||
      (progress.status === PassAtKRunStatus.Completed &&
        progress.totalTasks !== undefined &&
        progress.completedTasks !== progress.totalTasks &&
        onRetry)
    ) {
      return {
        text: 'Retry',
        onClick: onRetry,
        color: 'accent' as const,
        variant: 'surface' as const,
      };
    }

    return null;
  }, [progress, isOverTimeLimit, onRetry, onReactivate]);

  return (
    <div className="w-full flex flex-col">
      <Flex direction="row" gap="2">
        <div className="w-full flex flex-col">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-500">{getProgressTitle(progress)}</div>
            {!!formatElapsedTime(elapsedTime) && (
              <div className="text-xs text-gray-500 ml-2">{formatElapsedTime(elapsedTime)}</div>
            )}
          </div>
          <div className="h-3 w-full bg-neutral-200 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-300 ease-out bg-primary-400 relative"
              style={{ width: `${smoothProgress}%` }}
            >
              {/* Animated highlight for current stage */}
              <div
                className={`absolute inset-0 ${
                  progress?.status === PassAtKRunStatus.Completed &&
                  progress.totalTasks !== undefined &&
                  progress.completedTasks !== progress.totalTasks
                    ? 'bg-warning-500'
                    : progress?.status === PassAtKRunStatus.Error
                    ? 'bg-danger-500'
                    : progress?.status === PassAtKRunStatus.Cancelled
                    ? 'bg-neutral-400'
                    : 'bg-primary-500/50'
                }`}
              />

              {/* Animated diagonal stripes */}
              {!NOT_RUNNING_STATUSES.includes(progress?.status as PassAtKRunStatus) && (
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{
                    backgroundImage:
                      'linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent)',
                    backgroundSize: '1rem 1rem',
                    animation: 'progress-bar-stripes 1s linear infinite',
                  }}
                />
              )}
            </div>
          </div>

          {/* Add keyframes for the stripe animation */}
          <style jsx>{`
            @keyframes progress-bar-stripes {
              from {
                background-position: 1rem 0;
              }
              to {
                background-position: 0 0;
              }
            }
          `}</style>
        </div>
        {actionButton && (
          <Button
            size="1"
            color={actionButton.color}
            variant={actionButton.variant}
            onClick={actionButton.onClick}
            disabled={isUpdating}
          >
            {actionButton.text}
          </Button>
        )}
      </Flex>

      {/* Warning notification for runs over time limit */}
      {isOverTimeLimit && progress?.status === PassAtKRunStatus.Processing && (
        <div className="mt-2 p-2 bg-warning-50 rounded-md">
          <Text size="1" className="text-warning-900 font-medium">
            This run has been processing for over {WARNING_TIME_LIMIT_SECONDS / (60 * 60)} hours.
            You can reactivate the job to continue processing - your ongoing progress and results
            will be saved.
          </Text>
        </div>
      )}
    </div>
  );
}
